import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import './style.css';
import { COLOR_LEGEND_HEIGHT } from "../heatmap/constants";
import Dashboard from '../../model/Dashboard';
import {AllUSElectionData} from "../../model/data/StateElectionDataEntry"
import {USMapInteractionData, USMapTooltip} from "../geographic/USMapToolTip";

type BarChartProps = {
  data: AllUSElectionData[];//{ state: string; num_voters_per_electoral_vote: number; total_votes: number, year:number , dem_electoral_votes : number, rep_electoral_votes : number}[];
  width: number;
  height: number;
  yScale: d3.ScaleBand<string>;
  dashboard:Dashboard|null;
  updateSelectedStates: (states: Set<string>) => void,

//   xScale: d3.ScaleLinear<number, number>;
};

export type InteractionData = {
  state: string; 
  // num_voters_per_electoral_vote: number;
  total_votes: number;
  electoral_votes: number;
  x: number;
  y: number;
};


// const BarChart: React.FC = () => {
const BarChart: React.FC<BarChartProps> = ({ data, width, height, yScale, dashboard, updateSelectedStates}) => {
    const [hoveredCell, setHoveredCell] = useState<USMapInteractionData | null>(null);
    const year = dashboard?.year;
    const dataF = data.filter((row) => row.year === year)

    const normalizeStateName = (name: string | undefined | null): string => {
      if (typeof name !== 'string') {
        return ''; // Return an empty string or handle the error as needed
      }
      return name.replace(/\s/g, '');
    };

    function addSpaceBetweenLowerAndUpper(text: string): string {
      return text.replace(/([a-z])([A-Z])/g, "$1 $2");
    }

    const chartRef = useRef<SVGSVGElement | null>(null);

    useEffect(() => {

        function getStrokeWidth(d: any) {
            return dashboard?.selectedStates.has(normalizeStateName(d.state_name)) ? 1.5 : 0;
        }

        const chart = chartRef.current;
        if (!chart) return;

        const barHeight = yScale.bandwidth(); // Use the same bandwidth as the heatmap rows

    const xScale = d3.scaleLinear<number>()
    .domain([0, d3.max(dataF, (d) => 1/d.num_voters_per_electoral_vote) || 0])
    .range([0, width]); // width of the bar chart area

      // Clear any previous SVG contents before rendering
      d3.select(chart).selectAll("*").remove();
  
      const svg = d3.select(chart)
        .attr("width", width)
        .attr("height", height);

      const bars = svg.selectAll("g")
        .data(dataF)
        .enter()
        .append("g")
        .attr("class", "bar")
        .attr("transform", d => `translate(0, ${yScale(d.state_name)})`)  // Align bars with yScale from heatmap
        .on('mouseover', (event, d) => {

            const mousePos = { x: event.clientX, y: event.clientY };

            const usMapInteractionData: USMapInteractionData = {
                data: [
                    `${addSpaceBetweenLowerAndUpper(d.state_name)}: ${d.winner} won by ${d.point_lead}%`,
                    `Total votes: ${Math.round(d.total_votes/1000)}k`,
                    `Dem percentage: ${d.dem_percentage}%`,
                    `Rep percentage: ${d.rep_percentage}%`,
                    `Dem electoral votes: ${d.dem_electoral_votes}`,
                    `Rep electoral votes: ${d.rep_electoral_votes}`,
                    `Number of voters per electoral vote: ${Math.round(d.num_voters_per_electoral_vote/1000)}k`,
                    `Year: ${d.year}`
                ],
                value: 0,
                position: mousePos
            };

            setHoveredCell(usMapInteractionData);
            d3.select(event.currentTarget).select('rect').attr('fill', '#8338df');  // Highlight bar
          })
        //   .on('mousemove', (event, d) => {
        //     const [x, y] = d3.pointer(event);
        //     setTooltipPos({ x, y });  // Update tooltip position on mouse move
        //   })
          .on('mouseout', (event) => {
            setHoveredCell(null);  // Hide tooltip
            d3.select(event.currentTarget).select('rect').attr('fill', '#A18FDB');  // Reset bar color
          })
          
          .on('click', function (event, d) {
            const stateName = normalizeStateName(d.state_name);

            if (dashboard?.selectedStates.has(stateName)) {
                dashboard?.selectedStates.delete(stateName);
            } else {
                dashboard?.selectedStates.add(stateName);
            }
            if (dashboard){
              updateSelectedStates(dashboard?.selectedStates);}
        })
  
      bars.append("rect")
        .attr("x", d => width  - xScale(1/d.num_voters_per_electoral_vote)) // Position bars on the right
        .attr("height", barHeight)
        .attr("width", d => xScale(1/d.num_voters_per_electoral_vote))
        .attr('stroke', 'black')
        .attr('stroke-width', getStrokeWidth);
  
      // Append text labels (fruit names) inside the bars, centered
      bars.append("text")
        .attr("class", "label")
        .attr("x", width )
        .attr("y", barHeight / 2)
        .attr("dy", ".35em") // Vertical alignment middle
        .attr("fill", "white") // Labels inside bars should have good contrast
        .attr("text-anchor", "end")
        .text(d => addSpaceBetweenLowerAndUpper(d.state_name));
  
      // You can add more functionality, like appending values, axes, etc.
    }, [dashboard, data, dataF, height, updateSelectedStates, width, yScale]);
  
    return <>
    <svg ref={chartRef}></svg>
        <USMapTooltip
            data={hoveredCell?.data || []}
            position={hoveredCell?.position || { x: 0, y: 0 }}
            width={800}
            height={height - COLOR_LEGEND_HEIGHT}
        />
        </>;
  };
  
  export default BarChart;


